/**
 * React Query hooks for Storefront Theme CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { frappe } from "@/lib/frappe-client";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/config/routes";
import { getQueryOptions } from "@/lib/config/queryConfig";
import type { StorefrontThemeDoc } from "../types";
import { storefrontThemeQueryKeys } from "./queryKeys";

/**
 * Hook for fetching a single Storefront Theme detail
 */
export function useStorefrontThemeDetail(name: string) {
  return useQuery<StorefrontThemeDoc>({
    queryKey: storefrontThemeQueryKeys.detail(name),
    queryFn: () => frappe.get<StorefrontThemeDoc>({
      doctype: "Storefront Theme",
      name,
    }),
    enabled: !!name && name !== "new",
    ...getQueryOptions("detail"),
  });
}

/**
 * Hook for creating a new Storefront Theme
 */
export function useCreateStorefrontTheme() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<StorefrontThemeDoc, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return frappe.create<StorefrontThemeDoc>({
        doctype: "Storefront Theme",
        data,
      });
    },
    onSuccess: (data) => {
      const themeName = data.name;
      
      // Update cache for the new theme
      if (themeName) {
        queryClient.setQueryData(storefrontThemeQueryKeys.detail(themeName), data);
      }
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontThemeQueryKeys.list() });
      
      // Redirect to detail page
      if (themeName) {
        navigate(routes.storefrontTheme.detail(themeName));
      }
    },
  });
}

/**
 * Hook for saving an existing Storefront Theme
 */
export function useSaveStorefrontTheme(name: string, _originalData: Record<string, any>) {
  const queryClient = useQueryClient();

  return useMutation<StorefrontThemeDoc, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return frappe.update<StorefrontThemeDoc>({
        doctype: "Storefront Theme",
        name,
        data,
      });
    },
    onSuccess: (data) => {
      // Update cache
      queryClient.setQueryData(storefrontThemeQueryKeys.detail(name), data);
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontThemeQueryKeys.list() });
    },
    onError: (error) => {
      console.error('[useSaveStorefrontTheme] onError:', error);
    },
  });
}

/**
 * Hook for deleting a Storefront Theme
 */
export function useDeleteStorefrontTheme(name: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<void, Error, void>({
    mutationFn: () => {
      return frappe.delete({
        doctype: "Storefront Theme",
        name,
      });
    },
    onSuccess: () => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: storefrontThemeQueryKeys.detail(name) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontThemeQueryKeys.list() });
      // Navigate to list page
      navigate(routes.storefrontTheme.list);
    },
  });
}

/**
 * Hook for publishing a Storefront Theme
 */
export function usePublishStorefrontTheme() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; version?: string; error?: string },
    Error,
    { docname: string }
  >({
    mutationFn: async ({ docname }) => {
      return frappe.call<{ success: boolean; version?: string; error?: string }>(
        "zaviago_backend.storefront.publish.publish_theme",
        { docname }
      );
    },
    onSuccess: () => {
      // Invalidate detail query to refresh publish status fields
      queryClient.invalidateQueries({ queryKey: storefrontThemeQueryKeys.list() });
    },
  });
}
